import { useQuery } from '@tanstack/react-query';

import {
  companiesService,
  reportsService,
  payCalendarsService,
  FetchDeductionsReportsRequest,
  useToast,
} from '@aslanpay/portal-common-lib';

import { DeductionsReportPage } from 'app/components/UI/Table/Table.types';

const useFetchDeductionsReportsPage = (
  {
    ids,
    companyIds,
    payCalendarIds,
    payCycleIds,
    status,
    sortBy,
    sort,
    page,
    size,
  }: FetchDeductionsReportsRequest,
  enabled = true
) => {
  const toast = useToast();

  return useQuery<DeductionsReportPage, Error>(
    [
      'fetchDeductionsReportsPage',
      ids,
      companyIds,
      payCalendarIds,
      payCycleIds,
      status,
      sortBy,
      sort,
      page,
      size,
    ],
    async () => {
      const deductionsReports = await reportsService.fetchDeductionsReports({
        ids,
        companyIds,
        payCalendarIds,
        payCycleIds,
        status,
        sortBy,
        sort,
        page,
        size,
      });

      const companies = await companiesService.fetchCompanies({
        ids: deductionsReports.list.map((report) => report.companyId),
        page: 0,
        size: 999999,
      });

      const payCalendars = await payCalendarsService.fetchPayCalendars({
        ids: deductionsReports.list.map((report) => report.payCalendarId),
        page: 0,
        size: 999999,
      });

      const deductionsReportsList = deductionsReports.list.map((report) => {
        const companyName =
          companies.list.find((company) => company.id === report.companyId)?.name || '';
        const payCalendar = payCalendars.list.find(
          (calendar) => calendar.id === report.payCalendarId
        );
        const payCalendarName = payCalendar?.name || '';
        const payCycle = payCalendar?.payCycles?.find((cycle) => cycle.id === report.payCycleId);

        return {
          ...report,
          companyName,
          payCalendarName,
          payCycle,
          payCyclePeriod: payCycle?.period,
        };
      });

      return { ...deductionsReports, list: deductionsReportsList };
    },
    {
      enabled,
      onError: () => {
        toast({
          position: 'top-right',
          status: 'error',
          title: 'Something went wrong',
          description: `Sorry, we failed to fetch the reports. Please try again.`,
        });
      },
    }
  );
};

export default useFetchDeductionsReportsPage;
